import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Input } from '@artstorefronts/ui';
import { formatDistanceToNow } from 'date-fns';
import { MessagesSquare, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DOCK_LIST_MESSAGES_VARIABLES, MESSAGES_QUERY, MY_PROFILE_QUERY } from '~/graphql/queries';
import { getParticipantDisplayName } from '~/utils/participantHelpers';
import { useMessagingDock } from '~/context/messagingDock';
import { resolveTranslation } from '@arthelper/shared';
import { INBOX_MESSAGES_REFETCH_WRITE_POLICY } from '@arthelper/shared/inbox-messages-cache';
import { UserAvatar } from '~/components/UserAvatar';
import { DisabledUserName } from '~/components/DisabledUserName';

interface ParticipantUser {
  asfId: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profile?: {
    avatarUrl?: string | null;
    displayName?: string | null;
    isHumanVerified?: boolean | null;
    isDisabled?: boolean | null;
  } | null;
}

interface Participant {
  id: string;
  userId?: string;
  contactId?: string;
  user?: ParticipantUser | null;
  contact?: { id: string; name?: string | null; email?: string } | null;
}

interface ListMessage {
  id: string;
  channel: string;
  subject?: string | null;
  guild?: { id: string; slug: string; name: string } | null;
  participants: Participant[];
  lastMessagePart?: {
    content: string;
    createdAt: string;
    sourceLocale?: string | null;
    translations?: Array<{ locale: string; content: string; status: string }> | null;
  } | null;
  unreadCount: number;
  lastMessageAt: string;
}

export function DockListView() {
  const { t, i18n } = useTranslation('social');
  const userLocale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const { openThread } = useMessagingDock();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const myProfile = useQuery(MY_PROFILE_QUERY, { fetchPolicy: 'cache-first' });
  const viewerAsfId: string | undefined = myProfile.data?.profile?.user?.asfId?.toString();

  // Search runs on the SERVER across every thread, so the variables carry the term.
  // Spread it in only when there is one: Apollo matches refetchQueries by exact
  // variables, and the dock's refetch entries target the unsearched list.
  const variables = useMemo(
    () =>
      debouncedSearch ? { ...DOCK_LIST_MESSAGES_VARIABLES, search: debouncedSearch } : DOCK_LIST_MESSAGES_VARIABLES,
    [debouncedSearch]
  );

  const { data, loading, error, fetchMore } = useQuery<{ messages: { items: ListMessage[]; total: number } }>(
    MESSAGES_QUERY,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      // A reply refetches this list. Merge so the write keeps rows already paged in.
      refetchWritePolicy: INBOX_MESSAGES_REFETCH_WRITE_POLICY,
    }
  );

  // The server orders by real last activity and filters by `search`; the list renders
  // exactly what it is given. The old client-side filter/sort could only ever see the
  // first page, which is what made an older thread unreachable by either route.
  const items = data?.messages?.items ?? [];
  const total = data?.messages?.total ?? 0;
  const hasMore = items.length > 0 && items.length < total;

  const resolveOtherParticipants = (m: ListMessage): Participant[] => {
    const isInApp = m.channel?.toLowerCase() === 'in_app';
    if (isInApp) {
      return m.participants.filter((p) => p.user && viewerAsfId != null && p.user.asfId?.toString() !== viewerAsfId);
    }
    return m.participants.filter((p) => p.contact);
  };

  const firstNameOf = (p: Participant): string => {
    if (p.user?.firstName) return p.user.firstName;
    const display = p.user?.profile?.displayName || p.contact?.name;
    if (display) return display.split(/\s+/)[0] ?? display;
    return getParticipantDisplayName(p).split(/\s+/)[0] ?? 'Unknown';
  };

  const labelFor = (others: Participant[], guildName?: string | null): string => {
    // A guild room is called by the guild's name, however many people are in it.
    if (guildName) return guildName;
    if (others.length === 0) return t('common:unknown');
    if (others.length === 1) return getParticipantDisplayName(others[0]);
    const firsts = others.map(firstNameOf);
    if (firsts.length === 2) return `${firsts[0]}, ${firsts[1]}`;
    return `${firsts[0]}, ${firsts[1]} +${firsts.length - 2}`;
  };

  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    void fetchMore({ variables: { ...variables, offset: items.length } }).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [fetchMore, variables, items.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    // `root` is the scroll container, NOT the viewport. The dock is pinned to the
    // bottom of the screen, so a sentinel at the end of its own scroll area sits on
    // the viewport's edge and a default-root observer never reliably fires — which
    // is exactly what happened the first time this was driven in a browser.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('messages.search-placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            // 16px on phones: below that iOS zooms the whole page when the field is focused.
            className="pl-8 h-8 text-base md:text-sm"
          />
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {loading && items.length === 0 && (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
          </div>
        )}

        {error && !loading && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">{t('messages.failed-to-load')}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="px-4 py-10 text-center">
            <MessagesSquare className="size-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900">
              {searchQuery ? t('messages.no-messages-found') : t('messages.no-messages-yet')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {searchQuery ? t('messages.try-adjusting-search') : t('messages.not-started-yet')}
            </p>
          </div>
        )}

        <ul className="divide-y divide-gray-100">
          {items.map((m) => {
            const others = resolveOtherParticipants(m);
            const displayName = labelFor(others, m.guild?.name);
            const primary = others[0];
            const isGroup = others.length > 1;
            // Read the preview in the viewer's language too — a row that still showed
            // the sender's French while the thread rendered English would contradict itself.
            const snippet = m.lastMessagePart
              ? (resolveTranslation(
                  {
                    content: m.lastMessagePart.content,
                    sourceLocale: m.lastMessagePart.sourceLocale ?? undefined,
                    translations: m.lastMessagePart.translations ?? undefined,
                  },
                  userLocale
                ).content ?? '')
              : '';
            const unread = m.unreadCount > 0;
            const primaryUser = primary?.user;
            const primaryProfile = primaryUser?.profile ?? null;
            const isDisabled = !!primaryProfile?.isDisabled;
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => {
                    openThread(m.id, { source: 'dock_list' });
                  }}
                  className={`w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                    unread ? 'bg-blue-50/40' : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    {isGroup ? (
                      <GroupAvatarStack participants={others} />
                    ) : (
                      <UserAvatar
                        avatarUrl={primaryProfile?.avatarUrl ?? null}
                        firstName={primaryUser?.firstName}
                        lastName={primaryUser?.lastName}
                        displayName={primaryProfile?.displayName || primary?.contact?.name}
                        size={36}
                        isHumanVerified={!!primaryProfile?.isHumanVerified}
                        isDisabled={isDisabled}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-sm truncate ${
                          unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
                        }`}
                      >
                        {isDisabled ? <DisabledUserName /> : displayName}
                      </p>
                      <span className="text-[11px] text-gray-500 whitespace-nowrap flex-shrink-0">
                        {formatDistanceToNow(new Date(m.lastMessageAt), { addSuffix: true })}
                      </span>
                    </div>
                    {snippet && (
                      <p className={`text-xs truncate mt-0.5 ${unread ? 'text-gray-800' : 'text-gray-500'}`}>
                        {snippet}
                      </p>
                    )}
                  </div>
                  {unread && (
                    <span
                      className="mt-1.5 size-2 rounded-full bg-red-500 flex-shrink-0"
                      aria-label={t('messages.dock.unread-indicator', 'Unread')}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-4" aria-hidden="true">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Overlapping avatar stack (up to 3) for group-thread rows. */
function GroupAvatarStack({ participants }: { participants: Participant[] }) {
  const slice = participants.slice(0, 3);
  return (
    <div className="relative flex items-center" style={{ width: 32 + (slice.length - 1) * 14, height: 36 }}>
      {slice.map((p, i) => (
        <div
          key={(p.user?.asfId ?? p.contact?.id ?? i) + ''}
          className="absolute rounded-full ring-2 ring-white"
          style={{ left: i * 14, zIndex: slice.length - i }}
        >
          <UserAvatar
            avatarUrl={p.user?.profile?.avatarUrl ?? null}
            firstName={p.user?.firstName}
            lastName={p.user?.lastName}
            displayName={p.user?.profile?.displayName || p.contact?.name}
            size={28}
          />
        </div>
      ))}
    </div>
  );
}
